import { Zap, Bot, GitBranch, Play, type LucideIcon } from "lucide-react";

/**
 * Single source of truth for the per-node-type visual language, mirroring
 * the canvas node components (trigger=green, agent=accent, condition=amber,
 * action=purple). Used by the run timeline, run list, and workflow list so
 * the whole Workflows section reads as one design.
 */
export type WorkflowNodeKind = "trigger" | "agent" | "condition" | "action";

export interface NodeTheme {
  label: string;
  Icon: LucideIcon;
  /** text-<color> for the icon/label */
  text: string;
  /** bg-<color>/10 tint for chips + icon tiles */
  tint: string;
  /** solid bg-<color> for rail dots */
  solid: string;
  /** border-<color>/40 for accent borders */
  border: string;
}

const THEMES: Record<WorkflowNodeKind, NodeTheme> = {
  trigger: {
    label: "Trigger",
    Icon: Zap,
    text: "text-green-600",
    tint: "bg-green-500/10",
    solid: "bg-green-500",
    border: "border-green-500/40",
  },
  agent: {
    label: "Agent",
    Icon: Bot,
    text: "text-accent",
    tint: "bg-accent/10",
    solid: "bg-accent",
    border: "border-accent/40",
  },
  condition: {
    label: "Condition",
    Icon: GitBranch,
    text: "text-amber-600",
    tint: "bg-amber-500/10",
    solid: "bg-amber-500",
    border: "border-amber-500/40",
  },
  action: {
    label: "Action",
    Icon: Play,
    text: "text-purple-500",
    tint: "bg-purple-500/10",
    solid: "bg-purple-500",
    border: "border-purple-500/40",
  },
};

export function nodeTheme(kind: string): NodeTheme {
  return THEMES[kind as WorkflowNodeKind] ?? THEMES.action;
}

/** Visual treatment for a run/step status. */
export function runStatusStyles(status: string): {
  dot: string;
  text: string;
  tint: string;
  pulse: boolean;
} {
  switch (status) {
    case "completed":
      return { dot: "bg-emerald-500", text: "text-emerald-700 dark:text-emerald-300", tint: "bg-emerald-500/10", pulse: false };
    case "running":
      return { dot: "bg-blue-500", text: "text-blue-700 dark:text-blue-300", tint: "bg-blue-500/10", pulse: true };
    case "awaiting":
      return { dot: "bg-amber-500", text: "text-amber-700 dark:text-amber-300", tint: "bg-amber-500/10", pulse: true };
    case "failed":
      return { dot: "bg-rose-500", text: "text-rose-700 dark:text-rose-300", tint: "bg-rose-500/10", pulse: false };
    case "cancelled":
    case "skipped":
      return { dot: "bg-zinc-400", text: "text-zinc-600 dark:text-zinc-400", tint: "bg-zinc-500/10", pulse: false };
    default:
      return { dot: "bg-zinc-400", text: "text-text-muted", tint: "bg-zinc-500/10", pulse: false };
  }
}

/** Canvas-node treatment while a workflow run is selected/live. */
export function runNodeFrameClasses(status: string | undefined): string | null {
  switch (status) {
    case "completed":
      return "border-green-500/70 shadow-green-500/20 ring-2 ring-green-500/20";
    case "running":
      return "border-blue-400 ring-4 ring-blue-500/45 shadow-[0_0_0_10px_rgba(59,130,246,0.18),0_0_64px_rgba(59,130,246,0.75)] animate-pulse";
    case "awaiting":
      return "border-amber-400 ring-4 ring-amber-500/45 shadow-[0_0_0_10px_rgba(245,158,11,0.18),0_0_64px_rgba(245,158,11,0.65)] animate-pulse";
    case "failed":
      return "border-red-500/80 shadow-red-500/30 ring-2 ring-red-500/30";
    case "skipped":
    case "cancelled":
      return "border-gray-500/60 shadow-gray-500/20 ring-2 ring-gray-500/15 opacity-70";
    default:
      return null;
  }
}

export function runNodeDotClasses(status: string | undefined): string | null {
  switch (status) {
    case "completed":
      return "bg-green-500";
    case "running":
      return "bg-blue-500 animate-pulse";
    case "awaiting":
      return "bg-amber-500 animate-pulse";
    case "failed":
      return "bg-red-500";
    case "skipped":
    case "cancelled":
      return "bg-gray-500";
    default:
      return null;
  }
}

export function runNodeGlowClasses(status: string | undefined): string | null {
  switch (status) {
    case "running":
      return "bg-blue-500/25 shadow-[0_0_42px_rgba(59,130,246,0.7),0_0_96px_rgba(59,130,246,0.35)] animate-pulse";
    case "awaiting":
      return "bg-amber-500/25 shadow-[0_0_42px_rgba(245,158,11,0.65),0_0_96px_rgba(245,158,11,0.3)] animate-pulse";
    default:
      return null;
  }
}

export function runNodeBadgeLabel(status: string | undefined): string | null {
  switch (status) {
    case "awaiting":
      return "Waiting";
    default:
      return null;
  }
}

export function runNodeBadgeClasses(status: string | undefined): string | null {
  switch (status) {
    case "running":
      return "bg-blue-500 text-white shadow-[0_0_24px_rgba(59,130,246,0.6)]";
    case "awaiting":
      return "bg-amber-500 text-black shadow-[0_0_24px_rgba(245,158,11,0.55)]";
    default:
      return null;
  }
}

/**
 * Pull a human-readable headline out of a step's structured outputs so the
 * timeline can show prose instead of a JSON dump. Falls back to null.
 */
export function summarizeOutputs(
  outputs: Record<string, unknown> | null | undefined,
): string | null {
  if (!outputs || typeof outputs !== "object") return null;
  const keys = ["summary", "message", "text", "result", "content", "reasoning", "output"];
  for (const k of keys) {
    const v = (outputs as Record<string, unknown>)[k];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return null;
}
